import { COLORS, SCREEN_LG, SCREEN_MD } from "shared/consts";
import { adaptiveValue, toEm, toRem } from "shared/helpers";
import styled, { css } from "styled-components";

type PesticideMapWrapperProps = {
  $isFiltersOpen?: boolean;
}
export const PesticideMapWrapper = styled.div<PesticideMapWrapperProps>`
  position: relative;
  height: calc(100dvh - ${toRem(97)});
  margin-inline: ${toRem(-16)};
  @media (width < ${toEm(SCREEN_LG)}){
    padding-bottom: ${toRem(56)};
  }
  .ymaps3x0--controls_right {
    transition: all 0.3s ease 0s;
  }

  ${({$isFiltersOpen}) => $isFiltersOpen && css`
    .ymaps3x0--controls_right {
      right: ${toRem(400)};
    }
  `}
`

export const PesticideMapMarkerWrapper = styled.div`
  position: relative;
  --width: ${toRem(47)};
  --height: ${toRem(53)};

  @media (width < ${toEm(SCREEN_MD)}){
    --width: ${toRem(35)};
    --height: ${toRem(40)};
  }
`

export const PesticideMapMarker = styled.div`
  width: var(--width);
  height: var(--height);
  margin-top: calc((var(--height) / 2) * -1);
  margin-left: calc((var(--width) / 2) * -1);
  cursor: pointer;

  svg {
    max-width: 100%;
    max-height: 100%;
  }

  .polygon {
    transition: fill 0.3s ease 0s;
    fill: ${COLORS.ACCENT.DEFAULT};
  }

  @media (any-hover: hover){
    &:hover{
      .polygon {
        fill: ${COLORS.ACCENT.ACTIVE};
      }
    }
  }
`

type PesticideMapContentProps = {
  $isActive?: boolean
}
export const PesticideMapContent = styled.div<PesticideMapContentProps>`
  position: absolute;
  background-color: ${COLORS.BG.SURFACE1};
  border-radius: ${toRem(16)};
  box-shadow: 0px 32px 32px 0px ${COLORS.NEUTRAL.CONTAINER_SOFT}, 0px 0px 32px 0px ${COLORS.NEUTRAL.CONTAINER_MUTED};
  min-width: ${toRem(524)};
  padding: ${toRem(16)};
  transition: all 0.3s ease 0s;
  opacity: 0;
  pointer-events: none;
  z-index: 2000;


  @media (width >= ${toEm(SCREEN_MD)}){
    top: 50%;
    right: calc(100% + ${toRem(12)} + (var(--width) / 2));
    translate: ${toRem(50)} -50%;
  }

  @media (width < ${toEm(SCREEN_LG)}){
    min-width: ${toRem(374)};
  }

  @media (width < ${toEm(SCREEN_MD)}){
    right: 0;
    top: calc(100% + ${toRem(3)});
    min-width: ${toRem(295)};
    translate: 0 -50%;
  }

  ${props => props.$isActive && css`
    opacity: 1;
    pointer-events: all;
    @media (width >= ${toEm(SCREEN_MD)}){
      translate: 0 -50%;
    }
    @media (width < ${toEm(SCREEN_MD)}){
      translate: 0;
    }
  `}
`

export const PesticideMapContentHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${toRem(16)};
  &:not(:last-child) {margin-bottom: ${toRem(16)};}
`

export const PesticideMapContentTitle = styled.h3`
  font-weight: 700;
  ${adaptiveValue("font-size", 18, 14, 1440, 767)}
`

export const PesticideMapContentRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${toRem(16)};
  ${adaptiveValue("font-size", 16, 12, 1440, 767)}
  &:not(:last-child) {margin-bottom: ${toRem(16)};}

  span {
    color: ${COLORS.FG.DISABLED};
  }
`

export const PesticideMapClusterWrapper = styled.div`
  position: relative;
  --width: ${toRem(71)};
  --height: ${toRem(78)};
  width: var(--width);
  height: var(--height);
  margin-top: calc((var(--height) / 2) * -1);
  margin-left: calc((var(--width) / 2) * -1);
  cursor: pointer;

  svg {
    max-width: 100%;
    max-height: 100%;
  }

  span {
    color: ${COLORS.ACCENT.DEFAULT};
    position: absolute;
    top: 50%;
    left: 50%;
    translate: -50% -50%;
    font-weight: 700;
    ${adaptiveValue("font-size", 22, 16, 1440, 767)}
  }

  .polygon {
    fill: ${COLORS.BG.SURFACE1};
    transition: fill 0.3s ease 0s;
  }

  @media (any-hover: hover){
    &:hover{
      .polygon {
        fill: ${COLORS.BG.SURFACE3};
      }
    }
  }
`

export const PesticideMapPointFeatureWrapper = styled.div`
  --width: ${toRem(47)};
  --height: ${toRem(64)};
  width: var(--width);
  height: var(--height);
  margin-top: calc(var(--height) * -1);
  margin-left: calc((var(--width) / 2) * -1);
`

export const PesticideMapCreatePointWrapper = styled.form`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  height: 100dvh;
  
  @media (width >= ${toEm(SCREEN_MD)}){
    background-color: ${COLORS.BG.PAGE_FILLED};
  }
`

type PesticideMapCreatePointHeadProps = {
  $isMobile?: boolean;
}
export const PesticideMapCreatePointHead = styled.div<PesticideMapCreatePointHeadProps>`
  padding: ${toRem(16)};
  display: flex;
  flex-direction: ${props => !props.$isMobile ? 'row-reverse' : 'column'};
  gap: ${toRem(12)};
  justify-content: space-between;
  align-items: ${props => !props.$isMobile ? 'center' : 'initial'};
`

export const PesticideMapCreatePointTitle = styled.h2`
  display: flex;
  gap: ${toRem(12)};
  justify-content: space-between;
  font-weight: 700;
  font-size: ${toRem(22)};
  line-height: ${24 / 22};
  color: ${COLORS.FG.DEFAULT};

  @media (width < ${toRem(768)}){
    font-size: ${toRem(16)};
  }

  button {
    color: ${COLORS.ACCENT.DEFAULT};
  }
`

export const PesticideMapCreatePointBody = styled.div`
  padding: ${toRem(16)};
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: calc(100% - ${toRem(136)});
  overflow: auto;
  
  @media (width >= ${toRem(540)}){
    width: ${toRem(400)};
  }
  @media (width < ${toRem(540)}){
    width: 100vw;
  }
`

export const PesticideMapCreatePointItem = styled.div`
  border-radius: ${toRem(16)};
  padding: ${toRem(16)};
  box-shadow: 0px 2px 4px 0px ${COLORS.NEUTRAL.CONTAINER_SOFT}, 0px 0px 8px 0px ${COLORS.NEUTRAL.CONTAINER_MUTED};
  background-color: ${COLORS.BG.PAGE};
  &:not(:last-child) {margin-bottom: ${toRem(8)};}

  h3 {
    font-weight: 700;
    font-size: ${toRem(16)};
    line-height: ${20 / 16};

    &:not(:last-child) {margin-bottom: ${toRem(16)};}
  }

  .dropdown {
    width: 100%;
  }
`

export const PesticideMapCreatePointItemHead = styled.div`
  display: flex;
  gap: ${toRem(8)};
  justify-content: space-between;
  &:not(:last-child) {margin-bottom: ${toRem(16)};}

  a {
    font-size: ${toRem(14)};
    color: ${COLORS.ACCENT.DEFAULT};
    cursor: pointer;

    @media (any-hover: hover){
      &:hover{
        text-decoration: underline;
      }
    }
  }
`

export const PesticideMapCreatePointFooter = styled.div`
  padding: ${toRem(16)};
  display: flex;
  flex-direction: column;
  gap: ${toRem(12)};
`

export const PesticideMapDeletePointModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${toRem(8)};
`

export const PesticideMapDeletePointModalHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${toRem(8)};
  justify-content: space-between;
  width: 100%;

  h3 {
    font-weight: 700;
    font-size: ${toRem(16)};
  }
`

export const PesticideMapDeletePointModalText = styled.p`
  text-align: center;
  font-size: ${toRem(14)};
  line-height: ${20 / 14};
  color: ${COLORS.FG.SOFT};
  &:not(:last-child) {margin-bottom: ${toRem(8)};}
`

export const PesticideMapDeletePointModalFooter = styled.div`
  padding-top: ${toRem(12)};
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${toRem(8)};
`

type PesticideMapSidebarWrapperProps = {
  $isFiltersOpen?: boolean
}
export const PesticideMapSidebarWrapper = styled.div<PesticideMapSidebarWrapperProps>`
  position: absolute;
  transition: all 0.3s ease 0s;
  top: 0;
  right: ${({$isFiltersOpen}) => $isFiltersOpen ? toRem(400) : 0};
  padding: ${toRem(20)};
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${toRem(8)};
  justify-content: space-between;
  z-index: 2;
  pointer-events: none;

  &>* {
    pointer-events: all;
  }

  @media (width < ${toEm(SCREEN_LG)}){
    padding-bottom: ${toRem(76)};
  }
`

export const PesticideMapNoPolygonsModalWrapper = styled.div`
  display: flex;

  .button {
    margin-top: ${toRem(16)};
    margin-right: ${toRem(16)};
  }
`

export const PesticideMapNoPolygonsModalIcon = styled.div`
  padding: ${toRem(16)} ${toRem(12)};
  background-color: ${COLORS.INFO.CONTAINER_DEFAULT};
`

export const PesticideMapNoPolygonsModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${toRem(4)};
  padding: ${toRem(16)};
  ${adaptiveValue("font-size", 16, 13, 1440, 767)};
`

export const PesticideMapNoPolygonsModalTitle = styled.h3`
  font-weight: 700;
  font-size: ${toRem(16)};
  line-height: ${20 / 16};
`

export const PesticideMapNoPolygonsModalText = styled.div`
  color: ${COLORS.FG.SOFT};
  ${adaptiveValue("font-size", 16, 14, 1440, 767)};
`

type PesticideMapPolygonModalWrapperProps = {
  $left?: number | string;
  $top?: number | string;
  $isActive?: boolean;
}
export const PesticideMapPolygonModalWrapper = styled.div<PesticideMapPolygonModalWrapperProps>`
  position: fixed;
  transition: translate 0.3s ease 0s, opacity 0.3s ease 0s;
  translate: ${toRem(-50)} 0;
  opacity: 0;
  pointer-events: none;
  background-color: ${COLORS.BG.SURFACE1};
  box-shadow: 0px 32px 32px 0px ${COLORS.NEUTRAL.CONTAINER_SOFT}, 0px 0px 32px 0px ${COLORS.NEUTRAL.CONTAINER_MUTED};
  padding: ${toRem(16)};
  border-radius: ${toRem(16)};
  max-width: ${toRem(618)};
  z-index: 1001;

  ${({$isActive}) => $isActive && css`
    translate: none;
    opacity: 1;
    pointer-events: all;
  `}

  @media (width >= ${toEm(SCREEN_MD)}){
    top: ${({$top}) => $top || 0};
    left: ${({$left}) => $left || 0};
  }

  @media (width < ${toEm(SCREEN_MD)}){
    left: ${toRem(20)};
    bottom: ${toRem(76)};
    max-width: calc(100vw - ${toRem(40)});
    max-height: calc(100dvh - ${toRem(182)});
    overflow: clip auto;
  }
`

export const PesticideMapPolygonModalHead = styled.div`
  display: flex;
  gap: ${toRem(8)};
  align-items: center;
  justify-content: space-between;

  h3 {
    font-weight: 700;
    ${adaptiveValue("font-size", 18, 14, 1440, 767)}
  }

  &:not(:last-child) {
    ${adaptiveValue("margin-bottom", 16, 12, 1440, 767)}
  }
`

export const PesticideMapPolygonModalRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${toRem(16)};
  line-height: ${24 / 16};
  ${adaptiveValue("font-size", 16, 12, 1440, 767)}

  &:not(:last-child) {
    ${adaptiveValue("margin-bottom", 16, 12, 1440, 767)}
  }

  span {
    color: ${COLORS.FG.DISABLED};
  }

  p {
    @media (width < ${toEm(SCREEN_MD)}){
      font-weight: 500;
    }
  }
`